import React from 'react';
import { FileText, ListChecks, FormInput, History } from 'lucide-react';
import type { TaskDetailItem } from '../../shared/types/task';
import type { MinhaMesaTab } from '../types';
import { MinhaMesaInstructions } from './MinhaMesaInstructions';
import { MinhaMesaChecklist } from './MinhaMesaChecklist';
import { MinhaMesaFormFields } from './MinhaMesaFormFields';

interface MinhaMesaTaskDetailProps {
  task: TaskDetailItem;
  activeTab: MinhaMesaTab;
  onTabChange: (tab: MinhaMesaTab) => void;
  formState: Record<string, unknown>;
  checklistState: Record<string, boolean>;
  onFormFieldChange: (fieldId: string, value: unknown) => void;
  onChecklistToggle: (itemId: string) => void;
  logs: any[];
  logsLoading: boolean;
  isTaskStarted: boolean;
  visibleTabs?: MinhaMesaTab[];
  showHeader?: boolean;
  dense?: boolean;
}

const tabs: Array<{ id: MinhaMesaTab; label: string; icon: React.ReactNode }> = [
  { id: 'instrucoes', label: 'Instrucoes', icon: <FileText size={14} /> },
  { id: 'formulario', label: 'Formulario', icon: <FormInput size={14} /> },
  { id: 'checklist', label: 'Checklist', icon: <ListChecks size={14} /> },
  { id: 'historico', label: 'Historico', icon: <History size={14} /> },
];

export const MinhaMesaTaskDetail: React.FC<MinhaMesaTaskDetailProps> = ({
  task,
  activeTab,
  onTabChange,
  formState,
  checklistState,
  onFormFieldChange,
  onChecklistToggle,
  logs,
  logsLoading,
  isTaskStarted,
  visibleTabs,
  showHeader = true,
  dense = false,
}) => {
  const allowedTabs = visibleTabs ?? tabs.map((tab) => tab.id);

  return (
    <div className="flex flex-col h-full">
      {/* Task header */}
      {showHeader ? (
        <div className={`border-b border-slate-200 bg-transparent dark:border-slate-800 ${dense ? 'px-5 py-3' : 'px-6 py-4'}`}>
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
            <span className="font-mono">{task.referenceCode}</span>
            <span>-</span>
            <span>{task.instanceTitle}</span>
          </div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            {task.stageName}
          </h2>
          <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            {task.priority && (
              <span className="uppercase font-semibold">{task.priority}</span>
            )}
            <span>
              Tipo: {task.completionType === 'approve_reject' ? 'Aprovacao' : 'Execucao'}
            </span>
            {(task.assignedRoleName ?? task.metadata?.roleName ?? task.metadata?.role) && (
              <span>Responsavel estrutural: {task.assignedRoleName ?? task.metadata?.roleName ?? task.metadata?.role}</span>
            )}
            {(task.assignedUserId || task.assignedTo) && (
              <span>Executando: {task.assignedUserName ?? task.assignedToName ?? task.assignedUserId ?? '—'}</span>
            )}
          </div>
        </div>
      ) : null}

      {/* Tabs */}
      <div className={`flex border-b border-slate-200 bg-transparent dark:border-slate-800 ${dense ? 'px-0' : 'px-4'}`}>
        {tabs.map((tab) => {
          if (!allowedTabs.includes(tab.id)) return null;
          const isActive = tab.id === activeTab;
          // Hide empty tabs
          if (tab.id === 'formulario' && task.formFields.length === 0) return null;
          if (tab.id === 'checklist' && task.checklist.length === 0) return null;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex items-center gap-1.5 border-b transition-colors
                ${dense ? 'px-3 py-2 text-[12px]' : 'px-4 py-2.5 text-sm'}
                ${isActive
                  ? 'border-slate-900 bg-slate-100/60 text-slate-900 dark:border-slate-100 dark:bg-slate-900/60 dark:text-slate-100'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/40 hover:text-slate-700 dark:hover:text-slate-300'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className={`flex-1 overflow-y-auto ${dense ? 'px-0 py-4' : 'p-6'}`}>
        {activeTab === 'instrucoes' && (
          <MinhaMesaInstructions instruction={task.instruction} />
        )}

        {activeTab === 'formulario' && (
          <MinhaMesaFormFields
            fields={task.formFields}
            formState={formState}
            onFieldChange={onFormFieldChange}
            readOnly={!isTaskStarted}
          />
        )}

        {activeTab === 'checklist' && (
          <MinhaMesaChecklist
            items={task.checklist}
            checklistState={checklistState}
            onToggle={onChecklistToggle}
            readOnly={!isTaskStarted}
          />
        )}

        {activeTab === 'historico' && (
          <div className="space-y-3">
            {logsLoading ? (
              <p className="text-sm text-slate-400 animate-pulse">Carregando historico...</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                Nenhum registro no historico.
              </p>
            ) : (
              logs.map((log: any, idx: number) => (
                <div
                  key={log.id ?? idx}
                  className="flex items-start gap-3 text-sm border-l-2 border-slate-200 dark:border-slate-700 pl-4 py-1"
                >
                  <div className="flex-1">
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {log.action}
                    </span>
                    {log.justification && (
                      <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                        {log.justification}
                      </p>
                    )}
                  </div>
                  <time className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </time>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
